import { useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import 'trumbowyg/dist/ui/trumbowyg.min.css'
import { useEditorSync } from './hooks/useEditorSync'
import { useTrumbowygInit } from './hooks/useTrumbowygInit'
import { normalizeEditorContent } from './utils'

export interface TrumbowygEditorProps {
  value?: string
  onChange?: (html: string) => void
  placeholder?: string
  className?: string
  onKeyDown?: (e: KeyboardEvent) => void
  ariaDescribedBy?: string
  ref?: React.Ref<TrumbowygEditorRef>
}

export interface TrumbowygEditorRef {
  focus: () => void
}

// Exposes focus() via ref to enable edit message UX: click edit → load content → auto-focus editor
const TrumbowygEditor = ({
  value = '',
  onChange,
  placeholder,
  className,
  onKeyDown,
  ariaDescribedBy,
  ref,
}: TrumbowygEditorProps) => {
  const { t } = useTranslation()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const editorContainerRef = useRef<HTMLDivElement>(null)
  const normalizedValue = normalizeEditorContent(value)

  const onKeyDownRef = useRef(onKeyDown)
  // Prevent re-normalizing the same incoming non-normalized prop value repeatedly.
  const normalizedFromValueRef = useRef<string | null>(null)

  useEffect(() => {
    onKeyDownRef.current = onKeyDown
  }, [onKeyDown])

  const getEditorElement = useCallback(
    () => editorContainerRef.current?.querySelector('.trumbowyg-editor') as HTMLElement | null,
    []
  )

  const handleNormalizedChange = useCallback(
    (html: string) => {
      if (!onChange) return
      onChange(normalizeEditorContent(html))
    },
    [onChange]
  )

  // Places cursor at the end of the contentEditable so inserted text (undo send, edit) doesn't start at position 0.
  // Uses requestAnimationFrame to defer cursor placement until after useEditorSync has pushed content to the DOM.
  const focusEditor = useCallback(() => {
    const editor = getEditorElement()
    if (!editor) return
    editor.focus()
    requestAnimationFrame(() => {
      const selection = window.getSelection()
      if (selection && editor.childNodes.length > 0) {
        const range = document.createRange()
        range.selectNodeContents(editor)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      }
    })
  }, [getEditorElement])

  /**
   * Expose focus() via ref for programmatic focus control.
   * Enables parent to auto-focus editor after setting content,
   * avoiding manual click requirement for better UX.
   */
  useImperativeHandle(
    ref,
    () => ({
      focus: focusEditor,
    }),
    [focusEditor]
  )

  // Initialize Trumbowyg without paste sanitization - preserve all formatting
  const { isReady, isUpdatingRef, isInternalChangeRef } = useTrumbowygInit(textareaRef, {
    onChange: handleNormalizedChange,
  })

  // Programmatic value normalization at component boundary.
  useEffect(() => {
    if (!onChange || value === normalizedValue) {
      normalizedFromValueRef.current = null
      return
    }

    if (normalizedFromValueRef.current === value) {
      return
    }

    normalizedFromValueRef.current = value
    onChange(normalizedValue)
  }, [value, normalizedValue, onChange])

  // Sync external value changes to editor
  useEditorSync(textareaRef, normalizedValue, isReady, isUpdatingRef, isInternalChangeRef)

  // Sync DOM attributes on editor
  useEffect(() => {
    if (!isReady || !editorContainerRef.current) return
    const editorElement = getEditorElement()
    if (!editorElement) return

    // aria-describedby
    if (ariaDescribedBy) {
      editorElement.setAttribute('aria-describedby', ariaDescribedBy)
    } else {
      editorElement.removeAttribute('aria-describedby')
    }

    // placeholder
    if (placeholder) {
      editorElement.setAttribute('placeholder', placeholder)
    } else {
      editorElement.removeAttribute('placeholder')
    }
  }, [ariaDescribedBy, placeholder, isReady, getEditorElement])

  // Keydown wiring/cleanup
  useEffect(() => {
    const editor = getEditorElement()
    if (!editor) return
    const handler = (e: KeyboardEvent) => {
      // Shift+Enter or Ctrl/Cmd+Enter: insert line break
      if (e.key === 'Enter' && (e.shiftKey || e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        document.execCommand('insertLineBreak', false, undefined)
      }
      if (onKeyDownRef.current) {
        onKeyDownRef.current(e)
      }
    }
    editor.addEventListener('keydown', handler)
    return () => {
      editor.removeEventListener('keydown', handler)
    }
  }, [isReady, getEditorElement])

  return (
    <div
      ref={editorContainerRef}
      onClick={() => getEditorElement()?.focus()}
      onKeyDown={(e) => {
        // Only handle keyboard activation when the wrapper itself is focused (not events bubbling from the editor)
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          focusEditor()
        }
      }}
      role="button"
      tabIndex={0}
      className={className}
      style={{ display: 'contents' }}
      aria-label={t('chatInput.focus')}>
      <textarea ref={textareaRef} />
    </div>
  )
}

export { TrumbowygEditor }
